import {COMPANY_ID} from '@constants/auth.constant'
import {IMenuGroup, IMenuItem, MenuService} from '@services/api/menu.service'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {createContext, ReactNode, useContext, useEffect, useMemo, useState} from 'react'
import {useLang} from '_metronic/i18n/Metronici18n'

/**
 * Cache key is per company: the menu is filtered by the caller's role, and roles are
 * per-company, so a user who switches workspace must not be shown the previous one's
 * menu. Written with raw localStorage rather than LocalStorageService because the key
 * is dynamic and does not fit that service's fixed `ILocalStorageData` union; it is
 * still cleared by LocalStorageService.clear() on 401.
 */
const cacheKey = () => `hrmMenu:${LocalStorageService.get(COMPANY_ID) || 'none'}`

const readCache = (): IMenuGroup[] => {
  try {
    const raw = localStorage.getItem(cacheKey())
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writeCache = (menu: IMenuGroup[]) => {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(menu))
  } catch {
    /* quota or private mode — the menu still works, it just refetches next load */
  }
}

interface IAppMenuValue {
  menu: IMenuGroup[]
  loading: boolean
  /** Menu label for an exact route, or null when the route is not a menu item. */
  titleFor: (pathname: string) => string | null
}

const AppMenuContext = createContext<IAppMenuValue>({
  menu: [],
  loading: false,
  titleFor: () => null,
})

/**
 * The app menu, from sso `auth.features`, fetched once per session.
 *
 * A context rather than a plain hook because two separate consumers need it — the header
 * menu (MenuInner) and the page title (DefaultTitle) — and a per-caller hook would fetch
 * it twice on every page.
 *
 * Serves the cached copy immediately and refreshes in the background, so the header does
 * not go blank on every reload while a cross-origin SSO call is in flight. A failed
 * refresh keeps the cache rather than emptying the menu: losing navigation entirely is a
 * worse failure than showing a slightly stale copy.
 */
const AppMenuProvider = ({children}: {children: ReactNode}) => {
  const [menu, setMenu] = useState<IMenuGroup[]>(readCache)
  const [loading, setLoading] = useState<boolean>(() => readCache().length === 0)
  const lang = useLang()

  useEffect(() => {
    let alive = true
    MenuService.getAppMenu()
      .then((res: any) => {
        if (!alive) return
        const list: IMenuGroup[] = res?.body || []
        setMenu(list)
        writeCache(list)
      })
      .catch(() => {
        /* keep whatever the cache had */
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  // Exact url -> label. Exact, not prefix: a detail page like /hrm/employee/edit/:id must
  // keep its own heading rather than inheriting the list page's menu label.
  const byUrl = useMemo(() => {
    const map = new Map<string, string>()
    menu.forEach((g) =>
      g.children.forEach((it: IMenuItem) =>
        map.set(it.url, lang === 'bn' && it.titleBn ? it.titleBn : it.title)
      )
    )
    return map
  }, [menu, lang])

  const value: IAppMenuValue = {
    menu,
    loading,
    titleFor: (pathname: string) => byUrl.get(pathname.split(/[?#]/)[0]) ?? null,
  }

  return <AppMenuContext.Provider value={value}>{children}</AppMenuContext.Provider>
}

const useAppMenu = () => useContext(AppMenuContext)

export {AppMenuProvider, useAppMenu}
